// src/pages/TermOfServicePage.js
import React from 'react';
import LegalLayout from '../components/LegalLayout';

const sections = [
  { id: 'acceptance', title: 'Acceptance of Terms' },
  { id: 'description', title: 'Description of Service' },
  { id: 'responsibilities', title: 'User Responsibilities' },
  { id: 'intellectual-property', title: 'Intellectual Property' },
  { id: 'disclaimer', title: 'Disclaimer' },
  { id: 'limitation-liability', title: 'Limitation of Liability' },
  { id: 'termination', title: 'Termination' },
  { id: 'changes', title: 'Changes to Terms' },
  { id: 'contact', title: 'Contact Us' }
];

export default function TermOfServicePage() {
  return (
    <LegalLayout
      title="Terms of Service"
      lastUpdated="January 1, 2024"
      sections={sections}
    >
      <section id="acceptance">
        <h2>1. Acceptance of Terms</h2>
        <p>
          By accessing or using BloomPal, you agree to be bound by these Terms of Service
          and all applicable laws and regulations. If you do not agree with any of these
          terms, you are prohibited from using or accessing this service.
        </p>
        <p>
          These Terms constitute a legally binding agreement between you and Atem Infinity
          Inc. ("BloomPal," "we," "us," or "our"). Your use of BloomPal indicates your
          acceptance of these Terms.
        </p>
      </section>

      <section id="description">
        <h2>2. Description of Service</h2>
        <p>
          BloomPal provides AI-assisted wellness tools and mindfulness content designed to
          support stress relief, emotional wellbeing, and personal growth. Our platform
          includes features such as:
        </p>
        <ul>
          <li>Mood tracking and analysis</li>
          <li>Personalized wellness activities and mindfulness exercises</li>
          <li>AI-powered guidance and support</li>
          <li>Calendar integration for mindful scheduling</li>
          <li>Progress tracking and insights</li>
        </ul>
        <p>
          <strong>Important:</strong> BloomPal is not a substitute for professional
          medical, psychological, or psychiatric advice, diagnosis, or treatment. If you
          are experiencing a medical emergency, please call 911 or your local emergency
          services immediately.
        </p>
        <p>
          Our service is intended to complement, not replace, professional healthcare.
          Always seek the advice of qualified health providers with any questions you may
          have regarding a medical condition or mental health concern.
        </p>
      </section>

      <section id="responsibilities">
        <h2>3. User Responsibilities</h2>
        <p>
          As a user of BloomPal, you agree to the following responsibilities:
        </p>
        <ul>
          <li><strong>Accurate Information:</strong> You agree to provide accurate,
            current, and complete information when registering or interacting with the
            app. You are responsible for maintaining and updating this information.</li>
          <li><strong>Account Security:</strong> You are responsible for maintaining the
            confidentiality of your account credentials (username, password, etc.) and
            for all activities that occur under your account.</li>
          <li><strong>Lawful Use:</strong> You agree not to use the app for any unlawful
            or harmful purposes, including but not limited to:
            <ul>
              <li>Violating any applicable laws or regulations</li>
              <li>Infringing on the rights of others</li>
              <li>Transmitting harmful code, viruses, or malware</li>
              <li>Attempting to gain unauthorized access to our systems</li>
            </ul>
          </li>
          <li><strong>Respectful Conduct:</strong> You agree to use BloomPal in a
            respectful manner and not to harass, abuse, or harm other users or our
            staff.</li>
          <li><strong>Age Requirements:</strong> You must be at least 13 years old to use
            BloomPal. If you are under 18, you represent that you have your parent's or
            guardian's permission to use the service.</li>
        </ul>
      </section>

      <section id="intellectual-property">
        <h2>4. Intellectual Property</h2>
        <p>
          All content, design, functionality, and intellectual property of BloomPal,
          including but not limited to text, graphics, logos, icons, images, audio clips,
          digital downloads, data compilations, and software, are owned by Atem Infinity
          Inc. or its licensors and are protected by United States and international
          copyright, trademark, and other intellectual property laws.
        </p>
        <p>
          <strong>Limited License:</strong> We grant you a limited, non-exclusive,
          non-transferable, revocable license to access and use BloomPal for your personal,
          non-commercial use, subject to these Terms.
        </p>
        <p>
          <strong>Restrictions:</strong> You may not:
        </p>
        <ul>
          <li>Copy, modify, distribute, sell, or lease any part of BloomPal</li>
          <li>Reverse engineer or attempt to extract the source code of our software</li>
          <li>Remove any copyright, trademark, or other proprietary notices from our
            content</li>
          <li>Use our trademarks, logos, or brand elements without our prior written
            consent</li>
        </ul>
        <p>
          Any unauthorized use of our intellectual property may violate copyright, trademark,
          and other laws and may result in legal action.
        </p>
      </section>

      <section id="disclaimer">
        <h2>5. Disclaimer</h2>
        <p>
          BloomPal is provided "as is" and "as available" without warranties of any kind,
          either express or implied. To the fullest extent permitted by applicable law, we
          disclaim all warranties, including but not limited to:
        </p>
        <ul>
          <li>Warranties of merchantability, fitness for a particular purpose, and
            non-infringement</li>
          <li>Warranties that the service will be uninterrupted, timely, secure, or
            error-free</li>
          <li>Warranties regarding the accuracy, reliability, or completeness of any
            information or content provided through the service</li>
        </ul>
        <p>
          We do not guarantee that:
        </p>
        <ul>
          <li>The service will meet your specific requirements or expectations</li>
          <li>The service will be available at all times or free from errors</li>
          <li>Any defects or errors will be corrected</li>
          <li>The service is free from viruses or other harmful components</li>
        </ul>
        <p>
          Some jurisdictions do not allow the exclusion of implied warranties, so some of
          the above exclusions may not apply to you.
        </p>
      </section>

      <section id="limitation-liability">
        <h2>6. Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by applicable law, Atem Infinity Inc. and its
          officers, directors, employees, agents, and affiliates shall not be liable for
          any indirect, incidental, special, consequential, or punitive damages, including
          but not limited to:
        </p>
        <ul>
          <li>Loss of profits, revenue, data, or use</li>
          <li>Business interruption</li>
          <li>Personal injury or property damage</li>
          <li>Emotional distress</li>
        </ul>
        <p>
          This limitation applies regardless of the theory of liability (contract, tort,
          negligence, strict liability, or otherwise) and even if we have been advised of
          the possibility of such damages.
        </p>
        <p>
          In no event shall our total liability to you for all claims arising out of or
          relating to your use of BloomPal exceed the amount you paid to us, if any, in
          the twelve (12) months preceding the claim, or $100, whichever is greater.
        </p>
        <p>
          Some jurisdictions do not allow the limitation or exclusion of liability for
          incidental or consequential damages, so some of the above limitations may not
          apply to you.
        </p>
      </section>

      <section id="termination">
        <h2>7. Termination</h2>
        <p>
          We reserve the right to suspend or terminate your access to BloomPal at any time,
          with or without notice, for any reason, including but not limited to:
        </p>
        <ul>
          <li>Violation of these Terms of Service</li>
          <li>Fraudulent, abusive, or illegal activity</li>
          <li>Failure to pay any fees owed (if applicable)</li>
          <li>Extended periods of account inactivity</li>
          <li>At our sole discretion, for any other reason</li>
        </ul>
        <p>
          You may terminate your account at any time by contacting us at{' '}
          <a href="mailto:hello@bloompal-wellness.com">hello@bloompal-wellness.com</a> or
          through your account settings.
        </p>
        <p>
          Upon termination:
        </p>
        <ul>
          <li>Your right to use BloomPal will immediately cease</li>
          <li>We may delete or deactivate your account and all associated data</li>
          <li>Any provisions of these Terms that by their nature should survive
            termination will remain in effect</li>
        </ul>
      </section>

      <section id="changes">
        <h2>8. Changes to Terms</h2>
        <p>
          We may update these Terms of Service from time to time to reflect changes in our
          services, legal requirements, or business practices. We will notify you of any
          material changes by:
        </p>
        <ul>
          <li>Posting the updated Terms on this page with a new "Last updated" date</li>
          <li>Sending you an email notification (if you have provided an email address)</li>
          <li>Displaying a prominent notice within the BloomPal platform</li>
        </ul>
        <p>
          Your continued use of BloomPal after the effective date of any changes indicates
          your acceptance of the updated Terms. If you do not agree to the changes, you
          must stop using BloomPal and may terminate your account.
        </p>
        <p>
          We encourage you to review these Terms periodically to stay informed about your
          rights and obligations.
        </p>
      </section>

      <section id="contact">
        <h2>9. Contact Us</h2>
        <p>
          If you have questions about these Terms of Service, please contact us:
        </p>
        <p>
          <strong>Email:</strong>{' '}
          <a href="mailto:hello@bloompal-wellness.com">hello@bloompal-wellness.com</a>
        </p>
        <p>
          We will make every effort to respond to your inquiry within a reasonable time
          frame.
        </p>
        <p>
          <strong>Governing Law:</strong> These Terms shall be governed by and construed
          in accordance with the laws of the jurisdiction in which Atem Infinity Inc. is
          incorporated, without regard to its conflict of law provisions.
        </p>
      </section>
    </LegalLayout>
  );
}
